/**
 * As revisões anteriores de um documento (#344).
 *
 * Abre ao clicar na linha da listagem, e não expandindo a linha: a tabela do projeto não tem
 * linha expansível, e o histórico é leitura de exceção — quem vem à tela vem baixar o
 * documento vigente.
 *
 * Duas coisas que só aqui aparecem: a revisão SUBSTITUÍDA, que não é vencida (ela deixou de
 * ser o documento válido, e a data dela não vira alarme), e o assinante CONGELADO, que é quem
 * assinou na época — não o responsável que a empresa tem hoje.
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { ActionIconButton } from '../../../components/common';
import { documentService } from '../../../services/documentService';
import type {
  DocumentEmissionRecord,
  DocumentEmissionRevision,
  EmissionSignatorySnapshot,
} from '../../../types/document';
import { formatarDia, formatarPeriodo, situacaoDaEmissao } from '../situacao';
import { SituacaoChip } from './DocumentHistoryFilters';

/** O que a tela faz com o caminho do arquivo. O clique não pode subir para a linha. */
export type AoBaixar = (caminho: string, evento: React.MouseEvent) => void;

/**
 * Os dois arquivos de uma emissão, ou o recado de que eles não estão lá.
 *
 * Mora aqui porque a listagem e o diálogo mostram exatamente o mesmo par de botões, e a regra
 * do arquivo ausente — a emissão cujo registro nasceu e cuja gravação falhou — é uma só.
 */
export const ArquivosDaEmissao: React.FC<{
  pdfPath: string | null;
  docxPath: string | null;
  onBaixar: AoBaixar;
}> = ({ pdfPath, docxPath, onBaixar }) => {
  if (!pdfPath && !docxPath) {
    return (
      <Typography variant="caption" color="text.secondary" fontStyle="italic" noWrap>
        arquivo indisponível
      </Typography>
    );
  }

  return (
    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
      {pdfPath && (
        <ActionIconButton
          acao="document_emission.read"
          rotulo="Baixar PDF"
          size="small"
          onClick={(evento) => onBaixar(pdfPath, evento)}
        >
          <Typography variant="caption" fontWeight={700}>
            PDF
          </Typography>
        </ActionIconButton>
      )}
      {docxPath && (
        <ActionIconButton
          acao="document_emission.read"
          rotulo="Baixar Word"
          size="small"
          onClick={(evento) => onBaixar(docxPath, evento)}
        >
          <Typography variant="caption" fontWeight={700}>
            DOC
          </Typography>
        </ActionIconButton>
      )}
    </Stack>
  );
};

/** O assinante da revisão, como ele foi congelado — casado pelo nome do responsável técnico. */
function assinanteDa(revisao: DocumentEmissionRevision): EmissionSignatorySnapshot | undefined {
  const assinantes = revisao.signatureSnapshot?.signatories ?? [];
  return (
    assinantes.find((assinante) => assinante.name === revisao.technicalResponsibleName) ??
    assinantes[0]
  );
}

/** "Ana Beatriz Correia · CREA-SP 5063412 · emitido por Marcos Vinícius". */
function creditosDa(revisao: DocumentEmissionRevision): string {
  const assinante = assinanteDa(revisao);
  const partes: string[] = [];

  const nome = assinante?.name ?? revisao.technicalResponsibleName;
  if (nome) partes.push(nome);

  const registro = [assinante?.council, assinante?.registration].filter(Boolean).join(' ');
  if (registro) partes.push(registro);

  if (revisao.emittedByName) partes.push(`emitido por ${revisao.emittedByName}`);

  return partes.join(' · ');
}

function validadeDa(revisao: DocumentEmissionRevision): string {
  return revisao.validUntil === null
    ? 'sem validade definida'
    : `válido até ${formatarDia(revisao.validUntil)}`;
}

export interface RevisionsDialogProps {
  /** A linha clicada: dela sai a chave inteira, período incluído. */
  registro: DocumentEmissionRecord;
  onClose: () => void;
  onBaixar: AoBaixar;
}

export const RevisionsDialog: React.FC<RevisionsDialogProps> = ({
  registro,
  onClose,
  onBaixar,
}) => {
  const [revisoes, setRevisoes] = useState<DocumentEmissionRevision[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    let ativo = true;
    setCarregando(true);
    setErro(null);

    documentService
      .listRevisions({
        type: registro.type,
        scope: registro.scope,
        scopeId: registro.scopeId,
        periodStart: registro.periodStart,
        periodEnd: registro.periodEnd,
      })
      .then((lista) => {
        if (ativo) setRevisoes(lista);
      })
      .catch((err: unknown) => {
        if (ativo) setErro(err instanceof Error ? err.message : 'Não foi possível ler as revisões');
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    return () => {
      ativo = false;
    };
  }, [
    registro.type,
    registro.scope,
    registro.scopeId,
    registro.periodStart,
    registro.periodEnd,
  ]);

  const periodo = formatarPeriodo(registro.periodStart, registro.periodEnd);
  const quantas =
    revisoes.length === 1 ? 'Uma revisão emitida' : `${revisoes.length} revisões emitidas`;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="span" display="block">
          {`${registro.typeLabel} · ${registro.scopeName} · ${periodo}`}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {carregando
            ? 'Buscando as revisões desta chave…'
            : `${quantas}. Cada uma guarda o arquivo como foi entregue na época.`}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {carregando && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {erro && (
          <Alert severity="error" sx={{ m: 2 }}>
            {erro}
          </Alert>
        )}

        {!carregando &&
          !erro &&
          revisoes.map((revisao, indice) => {
            const situacao = situacaoDaEmissao(revisao);

            return (
              <Box key={revisao.id}>
                {indice > 0 && <Divider />}
                <Stack direction="row" spacing={2} alignItems="center" sx={{ px: 3, py: 1.5 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      flex: 'none',
                      borderRadius: 1,
                      border: 1,
                      borderColor: revisao.isLatest ? 'primary.main' : 'divider',
                      color: revisao.isLatest ? 'primary.main' : 'text.secondary',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1.1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={500}>
                      {revisao.revision}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: 9, letterSpacing: '.06em' }}>
                      REV
                    </Typography>
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2">
                      {`${formatarDia(revisao.emittedAt)} · ${validadeDa(revisao)}`}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {creditosDa(revisao)}
                    </Typography>
                  </Box>

                  <SituacaoChip situacao={situacao} />

                  <ArquivosDaEmissao
                    pdfPath={revisao.pdfPath}
                    docxPath={revisao.docxPath}
                    onBaixar={onBaixar}
                  />
                </Stack>
              </Box>
            );
          })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
      </DialogActions>
    </Dialog>
  );
};

export default RevisionsDialog;
